import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { Option } from 'types/Option';
import Flex from './Flex';
import { ReturnCenterDisplaySettingsResponse } from 'returnCenter-api';
import { IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

type TogglerProps = {
    valueLabelLeft: string;
    valueLabelRight: string;
    checked?: boolean;
    onClick: (checked: boolean) => void;
    size?: number;
    iconLeft?: IconDefinition;
    iconRight?: IconDefinition;
    className?: string;
    groupLabel?: string;
    siteSettings?: ReturnCenterDisplaySettingsResponse;
};
const Toggler = ({
    valueLabelLeft,
    valueLabelRight,
    iconLeft,
    iconRight,
    checked,
    onClick,
    size = 36,
    className,
    groupLabel,
    siteSettings,
    ...props
}: TogglerProps) => {
    const { t } = useTranslation();
    return (
        <Flex column className={`${className ? `${className} ` : ''} position-relative`}>
            <Grid
                className="f3-500"
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onClick(!checked);
                }}
                cols={2}
                role="group"
                aria-label={
                    groupLabel ||
                    t('action.toggleBetween', 'Toggle between {{left}} and {{right}}', {
                        left: valueLabelLeft,
                        right: valueLabelRight,
                    })
                }
            >
                <ToggleLabel size={size} active={!checked}>
                    {iconLeft ? <StyledFontAwesomeIcon icon={iconLeft} /> : null}
                    {valueLabelLeft}
                </ToggleLabel>
                <ToggleLabel size={size} active={!!checked}>
                    {iconRight ? <StyledFontAwesomeIcon icon={iconRight} /> : null}
                    {valueLabelRight}
                </ToggleLabel>
            </Grid>
            <TogglerWrapper size={size} checked={checked} {...props}>
                <ToggleButton
                    as="button"
                    cols={2}
                    siteSettings={siteSettings}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                />
            </TogglerWrapper>
        </Flex>
    );
};

type MultiTogglerProps<T> = {
    options: Option<T>[];
    checked?: T;
    onClick: (checked: T) => void;
    size?: number;
    className?: string;
    groupLabel?: string;
    siteSettings?: ReturnCenterDisplaySettingsResponse;
};
export const MultiToggler = <T,>({
    options,
    checked,
    onClick,
    size = 36,
    className,
    groupLabel,
    siteSettings,
    ...props
}: MultiTogglerProps<T>) => {
    const { t } = useTranslation();
    const checkedIndex = options.findIndex((o) => o.value === checked);
    return (
        <Wrapper column className={`${className ? `${className} ` : ''} position-relative`}>
            <Grid
                cols={options.length}
                className="f3-500"
                role="group"
                aria-label={groupLabel || t('action.toggleOptions', 'Toggle options')}
            >
                {options.map((option, i) => (
                    <MultiToggleButton
                        key={i.toString()}
                        size={size}
                        active={checked === option.value}
                        siteSettings={siteSettings}
                        onClick={() => {
                            if (checked === option.value) {
                                if (options.length !== 2) return;
                                const nextIndex = i + 1 < options.length ? i + 1 : 0;
                                const nextOption = options[nextIndex];
                                onClick(nextOption.value);
                            } else {
                                onClick(option.value);
                            }
                        }}
                    >
                        {option.label}
                    </MultiToggleButton>
                ))}
            </Grid>

            <TogglerWrapper size={size} checked={checkedIndex} {...props} cols={options.length}>
                <ToggleButton siteSettings={siteSettings} cols={options.length} $checked={checkedIndex} />
            </TogglerWrapper>
        </Wrapper>
    );
};

export default Toggler;

const Wrapper = styled(Flex)`
    background-color: transparent;
    border-radius: var(--radius);
`;
const ToggleButton = styled.span<{
    cols: number;
    siteSettings?: ReturnCenterDisplaySettingsResponse;
    $checked?: number;
}>`
    all: unset;
    position: absolute;
    top: 2px;
    left: 2px;
    display: block;
    height: calc(100% - 4px);
    width: calc(${({ cols }) => 100 / cols}% - 6px);

    background: ${({ theme, siteSettings }) => siteSettings?.accentThemeColor ?? theme.colors.accent};

    transition: left 0.2s ease-in-out;

    ${({ $checked }) => {
        if ($checked === 0) {
            return css`
                border-top-left-radius: var(--radius);
            `;
        } else if ($checked === 2) {
            return css`
                border-top-right-radius: var(--radius);
            `;
        } else {
            return css`
                border-radius: 0;
            `;
        }
    }}
    cursor: pointer;

    &:hover {
        background-color: ${({ theme }) => theme.colors.fillLight};
    }
`;

const Grid = styled.div<{ cols: number }>`
    all: unset;
    display: grid;
    grid-template-columns: repeat(${({ cols }) => cols}, 1fr);
    border: 0;
    padding: 0;
    gap: 2px;
`;

const TogglerWrapper = styled.div<{ checked?: boolean | number; size: number; cols?: number }>`
    position: absolute;
    top: 0;
    width: 100%;
    height: ${({ size }) => size}px;
    background: ${({ theme }) => theme.colors.fillMedium};
    color: ${({ theme }) => theme.colors.accent};

    border-top-right-radius: var(--radius);
    border-top-left-radius: var(--radius);

    z-index: 1;
    padding: 2px;

    ${({ checked }) =>
        typeof checked === 'boolean' &&
        checked &&
        `
      ${ToggleButton} {
        left: calc(50% + 4px);
      }
    `}

    ${({ checked, cols }) =>
        typeof checked === 'number' &&
        cols &&
        checked >= 1 &&
        `
      ${ToggleButton} {
        left: calc(${(100 / cols) * checked}% + 4px);
      }
    `}
`;

const toggleLabelStyles = (active: boolean, size: number) => css`
    all: unset;
    color: ${({ theme }) => (active ? theme.colors.white : theme.colors.textFaded)};
    padding: 0 1rem;
    box-sizing: border-box;
    width: 100%;
    height: ${size}px;
    line-height: ${size}px;
    margin: 0;
    font-family: inherit;
    text-align: center;
    z-index: 2;
    cursor: pointer;
    white-space: nowrap;
`;

const ToggleLabel = styled.label<{ active: boolean; size: number }>`
    ${({ active, size }) => toggleLabelStyles(active, size)}
`;

const MultiToggleButton = styled.button<{
    active: boolean;
    size: number;
    siteSettings?: ReturnCenterDisplaySettingsResponse;
}>`
    ${({ active, size }) => toggleLabelStyles(active, size)}
`;

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
    margin-right: 6px;
`;
